import { 
  Card, 
  CardHeader, 
  CardContent, 
  Typography, 
  IconButton,
  Box,
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { ResponsiveBar } from '@nivo/bar';
import { chartTheme } from './chartTheme';
import { useState, useRef, useEffect } from 'react';
import { DISTRIBUTION_CHART_COLORS } from './colorUtils';
import { useTheme } from '@mui/material/styles';

interface ChartData {
  name: string;
  total: number;
  statuses: {
    name: string;
    value: number;
    percentage: number;
    conversion_rate: number;
    converted_leads: number;
  }[];
}

interface ChannelStatusChartProps {
  title: string;
  data: ChartData[];
  onBarClick?: (item: string, type: 'channel' | 'status') => void;
}

// Helper function to truncate text
const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

// Custom hook for tracking hover state
const useHover = () => {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{x: number, y: number} | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  
  const handleMouseEnter = (item: string, x: number, y: number) => {
    // Clear any existing timeout to prevent state conflicts
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // If hovering a different item, update immediately
    if (hoveredItem !== item) {
      setHoveredItem(item);
      setHoverPosition({x, y});
    }
    
    // Make visible in all cases
    setIsVisible(true);
  };
  
  const handleMouseLeave = () => {
    setIsVisible(false);
    
    // Clear any existing timeout
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set a new timeout to clear the item after the animation completes
    timeoutRef.current = window.setTimeout(() => {
      setHoveredItem(null);
      setHoverPosition(null);
      timeoutRef.current = null;
    }, 300);
  };
  
  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return { hoveredItem, hoverPosition, isVisible, handleMouseEnter, handleMouseLeave };
};

export function ChannelStatusChart({ title, data, onBarClick }: ChannelStatusChartProps) {
  const theme = useTheme();
  
  // Add hover state for x-axis labels
  const { hoveredItem, hoverPosition, isVisible, handleMouseEnter, handleMouseLeave } = useHover();

  const handleClick = (bar: any) => {
    if (onBarClick) {
      onBarClick(bar.data.channel, 'channel');
    }
  };

  const chartData = data.map(channel => {
    const result: Record<string, any> = {
      channel: channel.name,
      totalLeads: channel.total,
    };
    
    channel.statuses.forEach(status => {
      result[status.name] = status.value;
      result[`${status.name}Percentage`] = status.percentage;
    });
    
    return result;
  });

  const keys = Array.from(
    new Set(data.flatMap(channel => channel.statuses.map(status => status.name)))
  );

  const handleExport = () => {
    const csvContent = [
      ['Channel', 'Total', ...keys].join(','),
      ...chartData.map(row => [
        row.channel,
        row.totalLeads,
        ...keys.map(key => row[key] || 0)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${title?.toLowerCase().replace(/\s+/g, '_') || 'chart'}.csv`;
    link.click();
  };

  // Calculate totals for tooltip percentages
  const calculatePercentages = () => {
    const grandTotal = data.reduce((sum, item) => sum + item.total, 0);
    const totals: Record<string, number> = {};
    
    data.forEach(item => {
      totals[item.name] = (item.total / grandTotal) * 100;
    });
    
    return totals;
  };

  const percentages = calculatePercentages();

  // Get metrics for a specific channel
  const getMetricsForChannel = (channelName: string) => {
    const channel = data.find(item => item.name === channelName);
    if (!channel) return { base: 0, converted: 0, rate: 0 };
    
    const totalBase = channel.total;
    const convertedLeads = channel.statuses.reduce((total, status) => 
      total + (status.conversion_rate > 0 ? status.converted_leads : 0), 0);
    const conversionRate = totalBase > 0 ? (convertedLeads / totalBase) * 100 : 0;
    
    return {
      base: totalBase,
      converted: convertedLeads,
      rate: conversionRate
    };
  };

  return (
    <Card sx={{ 
      height: 'auto',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      border: '1px solid rgba(0, 0, 0, 0.1)',
      borderRadius: 2,
      overflow: 'visible',
      position: 'relative'
    }}>
      <CardHeader
        title={
          <Typography 
            variant="h6" 
            component="h2"
            sx={{ 
              fontWeight: 700, 
              color: theme.palette.text.primary,
              fontSize: '1.25rem',
              letterSpacing: '0.01em',
              lineHeight: 1.3
            }}
          >
            {title}
          </Typography>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={handleExport}
              size="small"
              aria-label="Export data"
              title="Export data as CSV"
              sx={{ 
                color: '#36A2EB',
                '&:hover': {
                  backgroundColor: 'rgba(54, 162, 235, 0.1)'
                }
              }}
            >
              <FileDownloadIcon fontSize="small" />
            </IconButton>
          </Box>
        }
        sx={{
          '& .MuiCardHeader-action': {
            margin: 0,
            alignSelf: 'center',
          },
        }}
      />
      <CardContent sx={{ 
        height: 450,
        p: 2,
        '&:last-child': {
          pb: 2
        },
        position: 'relative' // Add for absolute positioning of the tooltip
      }}>
        <ResponsiveBar
          data={chartData}
          keys={keys}
          indexBy="channel"
          margin={{ top: 20, right: 160, bottom: 120, left: 60 }} // Increase bottom margin
          padding={0.2}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          colors={DISTRIBUTION_CHART_COLORS.slice(0, keys.length)}
          borderRadius={4}
          borderWidth={0}
          borderColor={{
            from: 'color',
            modifiers: [['darker', 0.6]]
          }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 30, // Increased padding to match other charts
            tickRotation: -45,
            legend: '',
            legendPosition: 'middle',
            legendOffset: 70,
            format: (value) => truncateText(value as string, 15),
            renderTick: ({ x, y, value, tickIndex }) => (
              <g 
                transform={`translate(${x},${y + 5})`} // Add 5px offset
                onMouseEnter={() => handleMouseEnter(String(value), x, y)}
                onMouseLeave={handleMouseLeave}
                style={{ cursor: 'pointer' }}
              >
                <text
                  textAnchor="end"
                  transform="rotate(-45)"
                  dominantBaseline="middle"
                  style={{
                    fill: '#666',
                    fontSize: 11,
                    fontWeight: hoveredItem === String(value) ? 'bold' : 'normal',
                    textDecoration: hoveredItem === String(value) ? 'underline' : 'none',
                  }}
                >
                  {truncateText(String(value), 15)}
                </text>
              </g>
            )
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Total Count',
            legendPosition: 'middle',
            legendOffset: -40,
            format: value => value.toLocaleString(),
            tickValues: []
          }}
          enableLabel={true}
          labelTextColor="#000000"
          labelSkipWidth={12}
          labelSkipHeight={12}
          legends={[
            {
              dataFrom: 'keys',
              anchor: 'bottom-right',
              direction: 'column',
              justify: false,
              translateX: 120,
              translateY: 0,
              itemsSpacing: 2,
              itemWidth: 100,
              itemHeight: 20,
              itemDirection: 'left-to-right',
              itemOpacity: 0.85,
              symbolSize: 20,
              symbolShape: 'square',
              itemTextColor: '#333333',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemOpacity: 1
                  }
                }
              ]
            }
          ]}
          layers={[
            'axes',
            'bars',
            'markers',
            'legends',
            'annotations'
          ]}
          role="application"
          ariaLabel="Channel status distribution chart"
          barAriaLabel={e => `${e.id}: ${e.formattedValue} in ${e.indexValue}`}
          theme={chartTheme}
          onClick={handleClick}
          tooltip={({ id, value, indexValue, color }) => (
            <div style={{
              background: 'white',
              padding: '12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontSize: '14px',
              maxWidth: '280px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
                gap: '8px'
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: color,
                  borderRadius: '2px'
                }} />
                <strong style={{ color }}>
                  {id}
                </strong>
              </div>
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '16px'
                }}>
                  <span style={{ color: '#666' }}>Count:</span>
                  <strong>{value.toLocaleString()}</strong>
                </div>
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '16px'
                }}>
                  <span style={{ color: '#666' }}>Channel:</span>
                  <strong>{indexValue}</strong>
                </div>
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '16px',
                  borderTop: '1px solid #eee',
                  marginTop: '4px',
                  paddingTop: '4px'
                }}>
                  <span style={{ color: '#666' }}>Contribution:</span>
                  <strong>{percentages[indexValue].toFixed(1)}%</strong>
                </div>
              </div>
            </div>
          )}
          animate={true}
          motionConfig="gentle"
        />

        {/* Tooltip for x-axis labels */}
        {hoveredItem && hoverPosition && (
          (() => {
            const metrics = getMetricsForChannel(hoveredItem);
            return (
              <div
                style={{
                  position: 'absolute',
                  bottom: 140, // Position above x-axis
                  left: hoverPosition.x, // Position directly above the hovered label
                  transform: 'translateX(-50%)', // Center the tooltip over the label
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  padding: '10px 16px',
                  zIndex: 9999,
                  minWidth: '160px',
                  textAlign: 'center',
                  opacity: isVisible ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out',
                  pointerEvents: 'none' // Prevent the tooltip from interfering with mouse events
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '14px', color: '#333' }}>
                  {hoveredItem}
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  gap: '10px',
                  borderTop: '1px solid #eee',
                  paddingTop: '8px',
                  marginTop: '3px' 
                }}>
                  <div style={{ fontSize: '12px', color: '#555' }}>Base:</div>
                  <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                    {metrics.base.toLocaleString()}
                  </div>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  gap: '10px',
                  marginTop: '3px' 
                }}>
                  <div style={{ fontSize: '12px', color: '#555' }}>Conv:</div>
                  <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                    {metrics.converted.toLocaleString()}
                  </div>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  gap: '10px',
                  marginTop: '3px',
                  color: '#007bff',
                  fontWeight: 'bold'
                }}>
                  <div style={{ fontSize: '12px' }}>Rate:</div>
                  <div style={{ fontSize: '12px' }}>
                    {metrics.rate.toFixed(1)}%
                  </div>
                </div>
              </div>
            );
          })()
        )}
      </CardContent>
    </Card>
  );
} 